import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User } from '../models/user';
import { UserAndAuthenticationApiClientService } from '../services/user-and-authentication-api-client.service';

export type AuthStatus = 'idle' | 'authenticated' | 'unauthenticated';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private httpClient = inject(HttpClient);
    private document = inject<Document>(DOCUMENT);

    readonly #userAndAuthenticationApiClient = inject(
        UserAndAuthenticationApiClientService,
    );
    readonly #router = inject(Router);

    public user$: BehaviorSubject<
        | {
              user: User;
              is_uaepass_session?: boolean;
          }
        | { user: null; is_uaepass_session?: boolean }
    > = new BehaviorSubject(JSON.parse(localStorage.getItem('gsr-user')!));
    public status$: BehaviorSubject<AuthStatus> = new BehaviorSubject('idle');
    private baseUrl: string;

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {
        this.baseUrl = environment.urls.api;
    }

    public get userValue() {
        return this.user$.value;
    }

    async refresh(navigateAfterRefresh = false) {
        const token = localStorage.getItem('gsr-user-token');
        if (!token) {
            this.user$.next({ user: null });
            this.status$.next('unauthenticated');
            return;
        }

        // TODO: better handling
        const currentUserResponse = await this.#userAndAuthenticationApiClient
            .getCurrentUser()
            .catch(({ error }: HttpErrorResponse) => {
                console.error(`error refreshing user -->`, error);
                localStorage.removeItem('gsr-user');
                localStorage.removeItem('gsr-user-token');
                this.user$.next({ user: null });
                this.#router.navigate(['/login']);
                return { user: null };
            });

        this.user$.next(currentUserResponse);

        if (navigateAfterRefresh) {
            this.#router.navigate(['/home']);
        }

        localStorage.setItem('gsr-user', JSON.stringify(currentUserResponse));
    }

    public authenticate() {
        this.refresh(true).then(() => {
            this.status$.next(
                this.user$.value ? 'authenticated' : 'unauthenticated',
            );
        });
    }

    public logout() {
        this.httpClient
            .post<{ logout_redirect: string }>(`${this.baseUrl}/logout/`, null)
            .subscribe((data) => {
                // remove user from local storage to log user out
                localStorage.removeItem('gsr-user');
                localStorage.removeItem('gsr-user-token');
                this.user$.next({ user: null });
                this.document.location.href = data.logout_redirect;
            });

        // this.document.location.href = "/";
    }
}
